import { storageService } from './storage.service';
import { storeService } from './store.service';
import { CMS_BASE_URL } from '../config/api.config';
import { readRegisterRefusal, VERIFICATION_EMAIL_FAILED } from './registerRefusal';
import { readSignInRefusal } from './signInRefusal';
import { rememberSignInMethod } from './lastSignInMethod';


// ===============================
// EMAIL AUTH
// ===============================

interface EmailAuthResponse {
  token: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    picture: string | null;
    isBlocked: boolean;
    companyId: number;
    mobileNumber: string | null;
    pets: any[];
    tags: any[];
    [key: string]: any;
  };
}

/**
 * The user record kept in storage is the person, not their pets or tags:
 * the login response carries every pet with its photos and notes, and
 * that blob is what blew the localStorage quota and, on a cookie-only
 * device, was too big for a cookie — so "cms_user" went missing while
 * "cms_jwt" stayed, and the routed pages read that as signed out. Pets
 * live in their own cache (setPetsCache).
 */
export const slimUser = (user: any): any => {
  if (!user || typeof user !== 'object') return user;
  const { pets, petProfiles, animals, tags, ...rest } = user;
  return rest;
};

const storeAuthResponse = (data: EmailAuthResponse): void => {
  storageService.setItem('cms_jwt', data.token);
  storageService.setItem('cms_user', JSON.stringify(slimUser(data.user)));
  if (data.user?.id) {
    storageService.setItem('cms_client_id', data.user.id.toString());
  }
  if (data.user?.pets && data.user.pets.length > 0) {
    setPetsCache(data.user.pets);
  }
};

export const emailRegister = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string
): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/email/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ firstName, lastName, email, password, companyId: await storeService.getCompanyId() })
  });

  if (!response.ok) {
    // A CMS and API from before G189 still say so; from then on every sign-up is one answer.
    if (response.status === 409) throw new Error('EMAIL_EXISTS');
    // The verification email couldn't go, so the API kept nothing (mpgadmin
    // G104): say so in its words, and the person can fix the address.
    const refusal = readRegisterRefusal(await response.json().catch(() => null));
    if (refusal.code === VERIFICATION_EMAIL_FAILED) {
      const err = new Error('REGISTER_EMAIL_UNSENT') as Error & { serverMessage?: string };
      err.serverMessage = refusal.message;
      throw err;
    }
    throw new Error('REGISTER_FAILED');
  }

  // A sign-up never signs in (G45; mpgadmin G188): the email link verifies
  // the address and the person signs in then. An older CMS still sends a
  // session here; it is ignored.
};

export const emailLogin = async (email: string, password: string): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/email/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, companyId: await storeService.getCompanyId() })
  });

  if (!response.ok) {
    // One answer for no account, no password yet and a wrong password (G45).
    const refusal = readSignInRefusal(response.status, await response.json().catch(() => null));
    if (refusal.kind === 'unverified') throw new Error('LOGIN_UNVERIFIED');
    if (refusal.kind === 'refused' || refusal.kind === 'locked') {
      const err = new Error(refusal.kind === 'locked' ? 'LOGIN_LOCKED' : 'LOGIN_REFUSED') as Error & { serverMessage?: string };
      err.serverMessage = refusal.message;
      throw err;
    }
    throw new Error('LOGIN_FAILED');
  }

  const data: EmailAuthResponse = await response.json();
  storeAuthResponse(data);
  rememberSignInMethod('email');
};

export const verifyEmailToken = async (token: string): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/email/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token })
  });

  if (!response.ok) throw new Error('INVALID_TOKEN');
};

export const forgotPassword = async (email: string): Promise<void> => {
  await fetch(`${CMS_BASE_URL}/api/auth/email/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, companyId: await storeService.getCompanyId() })
  });
  // Always succeeds — backend always returns 200 to prevent email enumeration
};

export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/email/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword })
  });

  if (!response.ok) throw new Error('INVALID_TOKEN');
};

export const resendVerification = async (email: string): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/email/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, companyId: await storeService.getCompanyId() })
  });

  if (!response.ok) throw new Error('RESEND_FAILED');
};

// ===============================
// PHONE (SMS OTP) AUTH
// ===============================

/**
 * Requests a one-time SMS code for the unified mobile continue flow —
 * allowSignup: true means unknown numbers get a real code too (they branch
 * into sign-up after verifying). Throws PHONE_CODE_COOLDOWN when the
 * per-number resend cooldown is still running (error.retryAfterSeconds
 * carries the wait).
 */
export const phoneSendCode = async (mobileNumber: string): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/phone/send-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobileNumber, companyId: await storeService.getCompanyId(), allowSignup: true })
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    // Two flavours of 429. "cooldown" means a code is already in flight for
    // this number, so the caller can go straight to the code screen.
    // "capacity" means the company's hourly send cap was hit and NO code was
    // sent — advancing would strand the user waiting for a text that never
    // comes, so it surfaces as its own error.
    const err = new Error(
      body?.reason === 'capacity' ? 'PHONE_CODE_CAPACITY' : 'PHONE_CODE_COOLDOWN'
    ) as Error & { retryAfterSeconds?: number; serverMessage?: string };
    err.retryAfterSeconds = body?.retryAfterSeconds ?? 60;
    err.serverMessage = typeof body?.error === 'string' ? body.error : undefined;
    throw err;
  }
  if (!response.ok) {
    // The backend's message says WHY (e.g. "SMS login is currently
    // unavailable. Please use email or Google instead.") — surface it
    // instead of a generic failure so the user isn't left guessing.
    const body = await response.json().catch(() => ({}));
    const err = new Error('PHONE_CODE_FAILED') as Error & { serverMessage?: string };
    err.serverMessage = typeof body?.error === 'string' ? body.error : undefined;
    throw err;
  }
};

/** One of the people who answer to a shared number. First name only. */
export interface PhoneClientChoice {
  clientId: number;
  firstName: string;
}

/**
 * Verifies the SMS code. Three outcomes:
 *
 * - Existing number, one account → session stored (same shape as email
 *   login), isNewUser false.
 * - New number → nothing stored; isNewUser true plus a one-shot signupToken
 *   for phoneCompleteSignup.
 * - Number several people share → nothing stored; `choices` lists them and
 *   `choiceToken` is redeemed by phoneChooseClient once the person says
 *   which one they are. The code proves they hold the phone, not which of
 *   them they are, and picking for them would open somebody else's pets.
 *
 * (Against a pre-choice backend the `choices` branch never occurs, so older
 * deployments behave exactly as before.)
 */
export const phoneVerifyCode = async (
  mobileNumber: string,
  code: string
): Promise<{
  isNewUser: boolean;
  signupToken?: string;
  choices?: PhoneClientChoice[];
  choiceToken?: string;
}> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/phone/verify-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobileNumber, code, companyId: await storeService.getCompanyId(), allowSignup: true })
  });

  if (!response.ok) {
    if (response.status === 401) throw new Error('PHONE_CODE_INVALID');
    throw new Error('PHONE_VERIFY_FAILED');
  }

  const data = await response.json();
  if (data?.isNewUser) {
    return { isNewUser: true, signupToken: data.signupToken };
  }
  if (data?.needsChoice) {
    return {
      isNewUser: false,
      choices: data.choices as PhoneClientChoice[],
      choiceToken: data.choiceToken as string,
    };
  }

  storeAuthResponse(data as EmailAuthResponse);
  rememberSignInMethod('phone');
  return { isNewUser: false };
};

/**
 * "It's me" — redeems the choice ticket for a session as one specific
 * client, after a shared number's code was verified.
 */
export const phoneChooseClient = async (
  mobileNumber: string,
  choiceToken: string,
  clientId: number
): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/phone/choose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mobileNumber,
      companyId: await storeService.getCompanyId(),
      choiceToken,
      clientId,
    })
  });

  if (!response.ok) {
    // The ticket is short-lived and single-use; expiry means start again
    // from the number, not retry the same choice.
    throw new Error('PHONE_CHOICE_EXPIRED');
  }

  storeAuthResponse((await response.json()) as EmailAuthResponse);
  rememberSignInMethod('phone');
};

/**
 * Finishes mobile sign-up after phoneVerifyCode returned isNewUser: creates
 * the account (first name required, rest optional) and stores the session.
 * Throws PHONE_SIGNUP_EXPIRED when the sign-up window has lapsed (start
 * over from the number screen) and PHONE_SIGNUP_EMAIL_EXISTS when the
 * optional email already belongs to an account.
 *
 * `offerTexts`: "Text me offers from {salon}" on Nearly there (MKT 16b,
 * GUARDRAILS G27). Only a tick the client made sends `acceptsSmsMarketing:
 * true`; unticked sends nothing, so it is never a yes made for them.
 */
export const phoneCompleteSignup = async (
  mobileNumber: string,
  signupToken: string,
  firstName: string,
  lastName?: string,
  email?: string,
  offerTexts = false
): Promise<void> => {
  const response = await fetch(`${CMS_BASE_URL}/api/auth/phone/complete-signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mobileNumber,
      companyId: await storeService.getCompanyId(),
      signupToken,
      firstName,
      lastName: lastName || undefined,
      email: email || undefined,
      ...(offerTexts === true ? { acceptsSmsMarketing: true } : {}),
    })
  });

  if (!response.ok) {
    if (response.status === 401) throw new Error('PHONE_SIGNUP_EXPIRED');
    if (response.status === 409) throw new Error('PHONE_SIGNUP_EMAIL_EXISTS');
    const body = await response.json().catch(() => ({}));
    const err = new Error('PHONE_SIGNUP_FAILED') as Error & { serverMessage?: string };
    err.serverMessage = typeof body?.error === 'string' ? body.error : undefined;
    throw err;
  }

  const data: EmailAuthResponse = await response.json();
  storeAuthResponse(data);
  rememberSignInMethod('phone');
};

// ===============================
// GOOGLE AUTH
// ===============================

export interface CMSAuthResponse {
  jwt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    picture: string;
    pets?: any[];
    petProfiles?: any[];
    animals?: any[];
    // Add other user fields as needed
    [key: string]: any; // Allow additional properties
  };
  pets?: any[]; // Will define this better after seeing the API response
  [key: string]: any; // Allow additional properties at root level
}

/**
 * Stores the session the One Tap frame posted (GoogleOneTap, DECISIONS D6).
 * The frame signs in on the CMS, which chose the business and environment
 * from this storefront's address; this only keeps what it sent.
 */
export const completeOneTapSignIn = (raw: any): CMSAuthResponse => {
  // Backend may return { jwt } (Google OAuth shape) or { token } (email auth shape)
  const jwt = raw?.jwt || raw?.token;
  if (!jwt || !raw?.user) throw new Error('One Tap response missing JWT');

  const data: CMSAuthResponse = { ...raw, jwt };

  storageService.setItem('cms_jwt', jwt);
  storageService.setItem('cms_user', JSON.stringify(slimUser(data.user)));
  if (data.user?.id) {
    storageService.setItem('cms_client_id', data.user.id.toString());
  }
  const pets = data.user?.pets || data.pets || data.user?.petProfiles || data.user?.animals;
  if (pets && pets.length > 0) {
    setPetsCache(pets);
  }
  rememberSignInMethod('google');

  return data;
};

export const decodeJWT = (jwt: string): any => {
  try {
    const base64Url = jwt.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

export const getCMSJWT = (): string | null => {
  return storageService.getItem('cms_jwt');
};

// Enhanced function that checks validity before returning JWT
export const getValidCMSJWT = (): string | null => {
  const { isAuthenticated } = checkAuthStatus();
  if (!isAuthenticated) {
    return null;
  }
  return storageService.getItem('cms_jwt');
};

// API wrapper that handles expired tokens
export const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // TEMP FIX: Use raw JWT to bypass checkAuthStatus validation
  const jwt = getCMSJWT();
  
  if (!jwt) {
    throw new Error('SESSION_EXPIRED'); // Special error code for expired sessions
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${jwt}`,
    ...options.headers
  };

  const response = await fetch(url, {
    // Always hit the network. The app manages its own freshness via the localStorage
    // cache layer, so letting the browser ALSO HTTP-cache these GETs caused stale reads:
    // after a reschedule/deposit the page re-fetched but the browser returned its cached
    // by-client response, so the change only appeared once the HTTP cache went stale.
    // Callers can still override by passing `cache` in options.
    cache: 'no-store',
    ...options,
    headers
  });

  // Handle expired token responses
  if (response.status === 401 || response.status === 403) {
    clearCMSAuth();
    throw new Error('SESSION_EXPIRED');
  }

  return response;
};

export const getCMSUser = (): CMSAuthResponse['user'] | null => {
  const userData = storageService.getItem('cms_user');
  return userData ? JSON.parse(userData) : null;
};

/**
 * The signed-in user for a routed page: the stored record when there is
 * one, else — while a token exists — the server's, stored slim for next
 * time. A page that decided "signed out" from a missing user record while
 * the token was right there showed a signed-in client the public flow.
 */
export const ensureCMSUser = async (): Promise<CMSAuthResponse['user'] | null> => {
  const stored = getCMSUser();
  if (stored) return stored;
  if (!getCMSJWT()) return null;
  try {
    const { fetchCurrentUserAndPets } = await import('./api');
    const { user, pets } = await fetchCurrentUserAndPets();
    if (!user?.id) return null;
    storageService.setItem('cms_user', JSON.stringify(slimUser(user)));
    storageService.setItem('cms_client_id', user.id.toString());
    if (pets && pets.length > 0) setPetsCache(pets);
    return user as CMSAuthResponse['user'];
  } catch (err) {
    console.error('Could not resolve the signed-in user from the server:', err);
    return null;
  }
};

export const clearCMSAuth = (): void => {
  storageService.removeItem('cms_jwt');
  storageService.removeItem('cms_user');
  storageService.removeItem('cms_client_id');
  storageService.removeItem('cms_pets');
  storageService.removeItem('cms_pets_timestamp');
};

export const setCMSClientId = (clientId: number): void => {
  storageService.setItem('cms_client_id', clientId.toString());
};

export const getCMSClientId = (): number | null => {
  const clientIdStr = storageService.getItem('cms_client_id');
  
  if (!clientIdStr) {
    // Try to extract from existing user data
    const userData = storageService.getItem('cms_user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.id) {
          storageService.setItem('cms_client_id', parsedUser.id.toString());
          return parseInt(parsedUser.id.toString(), 10);
        }
      } catch (error) {
        console.error('Error parsing cms_user data:', error);
      }
    }
    return null;
  }
  
  try {
    return parseInt(clientIdStr, 10);
  } catch (error) {
    console.error('Error parsing client ID:', error);
    return null;
  }
};

export const checkAuthStatus = () => {
  const jwt = getCMSJWT();
  const user = getCMSUser();
  
  
  if (!jwt || !user) {
    return { isAuthenticated: false, user: null };
  }
  
  try {
    const decoded = decodeJWT(jwt);
    if (!decoded) {
      return { isAuthenticated: false, user: null };
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    if (decoded.exp && decoded.exp < currentTime) {
      clearCMSAuth();
      return { isAuthenticated: false, user: null };
    }
    
    return { isAuthenticated: true, user };
  } catch (error) {
    console.error('Error checking auth status:', error);
    return { isAuthenticated: false, user: null };
  }
};

// Pets cache configuration
const PETS_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

export const getCMSPets = (): any[] => {
  const petsData = storageService.getItem('cms_pets');
  return petsData ? JSON.parse(petsData) : [];
};

export const getCMSPetsWithValidation = async (forceRefresh = false): Promise<any[]> => {
  const now = Date.now();
  const cachedPets = storageService.getItem('cms_pets');
  const cacheTimestamp = storageService.getItem('cms_pets_timestamp');
  
  // Check if cache is valid and not forcing refresh
  if (!forceRefresh && cachedPets && cacheTimestamp) {
    const timeDiff = now - parseInt(cacheTimestamp);
    if (timeDiff < PETS_CACHE_DURATION) {
      return JSON.parse(cachedPets);
    }
  }
  
  // Cache is stale, return existing cached data
  return getCMSPets();
};


export const updatePetsCache = (petResponse: any, operation: 'create' | 'update' | 'delete') => {
  try {
    const existingPets = storageService.getItem('cms_pets');
    let pets: any[] = existingPets ? JSON.parse(existingPets) : [];
    
    switch (operation) {
      case 'create':
        pets.push(petResponse);
        break;
      case 'update':
        const updateIndex = pets.findIndex(pet => pet.id === petResponse.id);
        if (updateIndex >= 0) {
          pets[updateIndex] = petResponse;
        }
        break;
      case 'delete':
        pets = pets.filter(pet => pet.id !== petResponse.id);
        break;
    }
    
    storageService.setItem('cms_pets', JSON.stringify(pets));
    storageService.setItem('cms_pets_timestamp', Date.now().toString());
  } catch (error) {
    console.error('Error updating pets cache:', error);
  }
};

export const setPetsCache = (pets: any[]) => {
  try {
    storageService.setItem('cms_pets', JSON.stringify(pets));
    storageService.setItem('cms_pets_timestamp', Date.now().toString());
  } catch (error) {
    console.error('Error setting pets cache:', error);
  }
};

// Public pet storage for non-authenticated users
export const getPublicPets = (): any[] => {
  try {
    const petsData = storageService.getItem('public_pets');
    return petsData ? JSON.parse(petsData) : [];
  } catch (error) {
    console.error('Error getting public pets:', error);
    return [];
  }
};

export const setPublicPets = (pets: any[]) => {
  try {
    storageService.setItem('public_pets', JSON.stringify(pets));
    storageService.setItem('public_pets_timestamp', Date.now().toString());
  } catch (error) {
    console.error('Error setting public pets:', error);
  }
};

export const addPublicPet = (pet: any) => {
  try {
    const existingPets = getPublicPets();
    const updatedPets = [...existingPets, pet];
    setPublicPets(updatedPets);
    return updatedPets;
  } catch (error) {
    console.error('Error adding public pet:', error);
    return getPublicPets();
  }
};

export const updatePublicPet = (index: number, updatedPet: any) => {
  try {
    const pets = getPublicPets();
    if (index >= 0 && index < pets.length) {
      pets[index] = { ...pets[index], ...updatedPet };
      setPublicPets(pets);
    }
    return pets;
  } catch (error) {
    console.error('Error updating public pet:', error);
    return getPublicPets();
  }
};

export const deletePublicPet = (index: number) => {
  try {
    const pets = getPublicPets();
    if (index >= 0 && index < pets.length) {
      pets.splice(index, 1);
      setPublicPets(pets);
    }
    return pets;
  } catch (error) {
    console.error('Error deleting public pet:', error);
    return getPublicPets();
  }
};

export const clearPublicPets = () => {
  try {
    storageService.removeItem('public_pets');
    storageService.removeItem('public_pets_timestamp');
  } catch (error) {
    console.error('Error clearing public pets:', error);
  }
};

export const hasPublicPets = (): boolean => {
  const pets = getPublicPets();
  return pets.length > 0;
}; 